/**
 * ============================================================================
 * GENERAL CONSTS
 * ============================================================================
 */

const CUSTOM_HTML_TAG = {
  A_SCRIPT: 'a-script',
  A_IFRAME: 'a-iframe',
  A_IMAGE: 'a-image',
  A_LINK: 'a-link',
  A_ENLARGEABLE_IMAGE: 'a-enlargeable-image',
  A_TABLE_INSIDE_ARTICLE: 'a-table-inside-article'
};

const CUSTOM_HTML_TAG_VALUES = Object.values(CUSTOM_HTML_TAG);

const TABLE_TAGS = {
  TABLE: 'table',
  THEAD: 'thead',
  TBODY: 'tbody',
  TH: 'th',
  TR: 'tr',
  TD: 'td'
};

const HTML_TAG = {
  ANCHOR: 'a',
  DIV: 'div',
  PARAGRAPH: 'p',
  SPAN: 'span',
  FIGURE: 'figure',
  FIGCAPTION: 'figcaption',
  IMAGE: 'img',
  HEADING_H1: 'h1',
  HEADING_H2: 'h2',
  HEADING_H3: 'h3',
  HEADING_H4: 'h4',
  HEADING_H5: 'h5',
  HEADING_H6: 'h6',
  LINE_BREAK: 'br',
  LINK: 'a',
  BOLD: 'b',
  ITALIC: 'i',
  BUTTON: 'button',
  SCRIPT: 'script',
  STYLE: 'style',
  IFRAME: 'iframe',
  SVG: 'svg',
  UL: 'ul',
  OL: 'ol',
  LI: 'li',
  BLOCKQUOTE: 'blockquote',
  COL: 'col',
  FONT: 'font',
  PRE: 'pre',
  MARK: 'mark',
  INPUT: 'input',
  SELECT: 'select',
  TEXTAREA: 'textarea',
  AUDIO: 'audio',
  ...TABLE_TAGS
};

const INLINE_HTML_TAGS = [
  HTML_TAG.LINK,
  HTML_TAG.BOLD,
  HTML_TAG.ITALIC,
  HTML_TAG.LINE_BREAK,
  HTML_TAG.BUTTON,
  HTML_TAG.IMAGE,
  HTML_TAG.SPAN,
  HTML_TAG.SCRIPT,
  HTML_TAG.MARK,
  HTML_TAG.INPUT,
  HTML_TAG.SELECT,
  'abbr',
  'acronym',
  'bdi',
  'bdo',
  'big',
  'cite',
  'data',
  'datalist',
  'del',
  'code',
  'dfn',
  'em',
  'embed',
  'font',
  'ins',
  'kbd',
  'label',
  'map',
  'meter',
  'object',
  'output',
  'progress',
  'ruby',
  'q',
  'samp',
  'small',
  'strong',
  'sub',
  'svg',
  'sup',
  'template',
  'time',
  'tt',
  'var',
  'u',
  's',
  'wbr'
];

const SPECIAL_CHAR = {
  LINE_FEED: String.fromCharCode(10), // LF
  CARRIAGE_RETURN: String.fromCharCode(13), // CR
  NON_BREAKING_SPACE: String.fromCharCode(160), // &nbsp;
  NON_BREAKING_SPACE_TEXT: '&nbsp;',
  SPACE: ' ',
  NEXT_LINE: '\\n',
  BACK_SLASH: '\\',
  DOUBLE_QUOTE: '"'
};

const EMPTY_TEXT_CHARS = [
  SPECIAL_CHAR.LINE_FEED,
  SPECIAL_CHAR.CARRIAGE_RETURN,
  SPECIAL_CHAR.NEXT_LINE
];

const INLINE_EXCLUSION_ELEMENTS_TO_UNWRAP_FROM_PARAGRAPH = [
  HTML_TAG.IFRAME
];

const CHARS_TO_ESCAPE = [SPECIAL_CHAR.BACK_SLASH];

const REMOVE_EMPTY_TAGS_EXCLUSION_TAG_LIST = [
  HTML_TAG.SCRIPT,
  CUSTOM_HTML_TAG.A_SCRIPT,
  CUSTOM_HTML_TAG.A_TABLE_INSIDE_ARTICLE,
  HTML_TAG.FIGURE,
  HTML_TAG.IMAGE,
  CUSTOM_HTML_TAG.A_IMAGE,
  CUSTOM_HTML_TAG.A_ENLARGEABLE_IMAGE,
  CUSTOM_HTML_TAG.A_LINK,
  HTML_TAG.IFRAME,
  CUSTOM_HTML_TAG.A_IFRAME,
  HTML_TAG.LINE_BREAK,
  HTML_TAG.MARK,
  HTML_TAG.AUDIO
];

const REPLACE_DIV_WITH_PARAGRAPH_EXCLUSION_PARENT_TAG_LIST = [
  HTML_TAG.FIGURE
];

const WRAP_INLINE_ELEMENTS_TO_PARAGRAPH_EXCLUSION_TAG_LIST = [
  CUSTOM_HTML_TAG.A_SCRIPT,
  HTML_TAG.SCRIPT,
  CUSTOM_HTML_TAG.A_TABLE_INSIDE_ARTICLE
];

const CHARS_TO_SPLIT_PARAGRAPHS = [
  SPECIAL_CHAR.LINE_FEED,
  SPECIAL_CHAR.NEXT_LINE
];

const REMOVE_INLINE_STYLES_EXCLUSION_TAG_LIST = [HTML_TAG.FIGURE];

const COMMENTED_HTML_REGEXP_STRING = '<!--.*?-->';
const COMMENTED_HTML_REGEXP = new RegExp(
  COMMENTED_HTML_REGEXP_STRING,
  'gmis'
);

const TABLE_INSIDE_ARTICLE_DATA_ATTRIBUTE = 'tableData';

const SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING = {
  ANY_CONTENT_LAZY: '.*?',
  IMG: '<img[^<>]*\\/?>',
  LINK_OPEN: '<a [^<>]*?>',
  LINK_CLOSE: '<\\/a>',
  FIGURE_OPEN: '<figure[^<>]*?>',
  FIGURE_CLOSE: '<\\/figure>',
  CAPTION_OPEN: '\\[caption[^\\]]*?\\]',
  CAPTION_CLOSE: '\\[\\/caption\\]',
  CAPTION_ALIGN: 'align="(.*?)"',
  CAPTION_CONTENT: '(.*<img[^>]+>)(.+)?$'
};

/**
 * ============================================================================
 * IMAGE CONSTS
 * ============================================================================
 */

const IMAGE_ALIGNMENT = {
  RIGHT: 'RIGHT',
  LEFT: 'LEFT',
  CENTER: 'CENTER',
  NORMAL: 'NORMAL'
};

const LEGACY_ALIGN_VALUE = {
  ALIGN_LEFT: 'alignleft',
  ALIGN_RIGHT: 'alignright',
  ALIGN_CENTER: 'aligncenter' // just an assumption
};

const IMAGE_SETTINGS = {
  CLASS_NAMES: {
    FIGURE: 'content-data__image',
    FIGCAPTION: 'figure-img__c',
    FIGCAPTION_CONTENT: 'content-data__caption',
    IMAGE: 'media-center__image',
    TEXT_ALIGNMENT: []
  },
  ATTRIBUTES: {
    DATA_MEDIA_ID: 'data-media-id',
    DATA_SRC: 'data-src',
    WRAPPER_CLASS: 'wrapper-class',
    ASPECT_RATIO: 'aspect-ratio',
    LAYOUT: 'layout',
    SRCSET_LOCATION: 'srcset-location'
  },
  STYLES: {
    FLOAT_LEFT: 'float: left',
    FLOAT_RIGHT: 'float: right',
    MARGIN_LEFT_AUTO: 'margin-left: auto',
    MARGIN_RIGHT_AUTO: 'margin-right: auto'
  },
  ALIGNMENT: {
    ALIGNMENT_BY_LEGACY_CAPTION_ALIGN_VALUE: {
      [LEGACY_ALIGN_VALUE.ALIGN_LEFT]: IMAGE_ALIGNMENT.LEFT,
      [LEGACY_ALIGN_VALUE.ALIGN_RIGHT]: IMAGE_ALIGNMENT.RIGHT,
      [LEGACY_ALIGN_VALUE.ALIGN_CENTER]: IMAGE_ALIGNMENT.CENTER
    },
    LEGACY_IMAGE_CLASS_NAME_BY_ALIGNMENT: {
      [IMAGE_ALIGNMENT.LEFT]: 'alignleft',
      [IMAGE_ALIGNMENT.RIGHT]: 'alignright',
      [IMAGE_ALIGNMENT.CENTER]: 'aligncenter'
    },
    FIGURE_CLASS_BY_ALIGNMENT: {
      [IMAGE_ALIGNMENT.LEFT]: 'align-left',
      [IMAGE_ALIGNMENT.RIGHT]: 'align-right',
      [IMAGE_ALIGNMENT.CENTER]: 'align-center'
    }
  }
};

const TARGET = {
  SELF: '_self',
  BLANK: '_blank'
};

const TWITTER_CARD_SCRIPT_SOURCE =
  'https://platform.twitter.com/widgets.js';
const TWITTER_CARD_SCRIPT = `<script async src="${TWITTER_CARD_SCRIPT_SOURCE}" charset="utf-8"></script>`;

const TWITTER_CARD_CLASS_NAME = 'twitter-tweet';

const REL_ATTRIBUTE_BACKEND_ENUM = {
  NONE: 'None',
  ALTERNATE: 'Alternate',
  AUTHOR: 'Author',
  FOLLOW: 'Follow',
  NOFOLLOW: 'Nofollow',
  BOOKMARK: 'Bookmark',
  EXTERNAL: 'External',
  HELP: 'Help',
  LICENSE: 'License',
  NEXT: 'Next',
  NOOPENER: 'Noopener',
  NOREFERRER: 'Noreferrer',
  PREV: 'Prev',
  SEARCH: 'Search',
  TAG: 'Tag'
};

const HTML_REL_ATTRIBUTE_VALUE = {
  ALTERNATE: 'alternate',
  AUTHOR: 'author',
  NOFOLLOW: 'nofollow',
  BOOKMARK: 'bookmark',
  EXTERNAL: 'external',
  HELP: 'help',
  LICENSE: 'license',
  NEXT: 'next',
  NOOPENER: 'noopener',
  NOREFERRER: 'noreferrer',
  PREV: 'prev',
  SEARCH: 'search',
  TAG: 'tag'
};

const HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM = {
  [REL_ATTRIBUTE_BACKEND_ENUM.NONE]: '',
  [REL_ATTRIBUTE_BACKEND_ENUM.ALTERNATE]: HTML_REL_ATTRIBUTE_VALUE.ALTERNATE,
  [REL_ATTRIBUTE_BACKEND_ENUM.AUTHOR]: HTML_REL_ATTRIBUTE_VALUE.AUTHOR,
  [REL_ATTRIBUTE_BACKEND_ENUM.FOLLOW]: '',
  [REL_ATTRIBUTE_BACKEND_ENUM.NOFOLLOW]: HTML_REL_ATTRIBUTE_VALUE.NOFOLLOW,
  [REL_ATTRIBUTE_BACKEND_ENUM.BOOKMARK]: HTML_REL_ATTRIBUTE_VALUE.BOOKMARK,
  [REL_ATTRIBUTE_BACKEND_ENUM.EXTERNAL]: HTML_REL_ATTRIBUTE_VALUE.EXTERNAL,
  [REL_ATTRIBUTE_BACKEND_ENUM.HELP]: HTML_REL_ATTRIBUTE_VALUE.HELP,
  [REL_ATTRIBUTE_BACKEND_ENUM.LICENSE]: HTML_REL_ATTRIBUTE_VALUE.LICENSE,
  [REL_ATTRIBUTE_BACKEND_ENUM.NEXT]: HTML_REL_ATTRIBUTE_VALUE.NEXT,
  [REL_ATTRIBUTE_BACKEND_ENUM.NOOPENER]: HTML_REL_ATTRIBUTE_VALUE.NOOPENER,
  [REL_ATTRIBUTE_BACKEND_ENUM.NOREFERRER]: HTML_REL_ATTRIBUTE_VALUE.NOREFERRER,
  [REL_ATTRIBUTE_BACKEND_ENUM.PREV]: HTML_REL_ATTRIBUTE_VALUE.PREV,
  [REL_ATTRIBUTE_BACKEND_ENUM.SEARCH]: HTML_REL_ATTRIBUTE_VALUE.SEARCH,
  [REL_ATTRIBUTE_BACKEND_ENUM.TAG]: HTML_REL_ATTRIBUTE_VALUE.TAG
};

/**
 * This is a technical/legacy value that is not a valid rel attribute.
 * We use it for backwards compatibility purposes to understand if the
 * intention is to force that a certain link is followed by the crawlers.
 */
const HTML_REL_ATTRIBUTE_FOLLOW_VALUE = 'follow';

const REL_ATTRIBUTE_BACKEND_ENUM_BY_LEGACY_HTML_REL_ATTRIBUTE = {
  [HTML_REL_ATTRIBUTE_FOLLOW_VALUE]: REL_ATTRIBUTE_BACKEND_ENUM.FOLLOW
};

const BACKEND_ENUM_BY_HTML_REL_ATTRIBUTE_VALUE = Object.entries(
  HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM
)
  .filter(([, value]) => value)
  .reduce((accumulator, [key, value]) => {
    accumulator[value] = key;
    return accumulator
  }, {});

const BASE_REL_ATTRIBUTE_BACKEND_VALUES = [
  REL_ATTRIBUTE_BACKEND_ENUM.NOOPENER,
  REL_ATTRIBUTE_BACKEND_ENUM.NOREFERRER
];

const BASE_HTML_REL_ATTRIBUTE_VALUES = BASE_REL_ATTRIBUTE_BACKEND_VALUES.map(
  (rel) => HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM[rel]
);

const KEY_CODE = {
  SPACEBAR: 32,
  PAGEUP: 33,
  PAGEDOWN: 34,
  END: 35,
  HOME: 36,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  ENTER: 13,
  ESCAPE: 27,
  TAB: 9
};

const IMAGE_BASE_64_REGEXP = /^data:image\/.+;base64,/;

const MEDIA_TYPE = {
  IMAGE: 'Image',
  VIDEO: 'Video',
  PDF: 'PDF',
  MS_WORD: 'MsWord',
  MS_EXCEL: 'MsExcel',
  CSV: 'Other',
  UNKNOWN: 'Unknown'
};

const FILE_EXTENSION = {
  PNG: '.png',
  JPEG: '.jpeg',
  JPG: '.jpg',
  GIF: '.gif',
  MP4: '.mp4',
  PDF: '.pdf',
  DOC: '.doc',
  DOCX: '.docx',
  XLS: '.xls',
  XLSX: '.xlsx',
  CSV: '.csv'
};

const MEDIA_TYPE_FORMATS = {
  [MEDIA_TYPE.IMAGE]: (isOld) => {
    if (isOld) {
      return [
        FILE_EXTENSION.JPEG,
        FILE_EXTENSION.JPG,
        FILE_EXTENSION.GIF,
        FILE_EXTENSION.PNG
      ]
    } else {
      return [FILE_EXTENSION.JPEG, FILE_EXTENSION.JPG, FILE_EXTENSION.GIF]
    }
  },
  [MEDIA_TYPE.VIDEO]: () => ['.mp4'],
  [MEDIA_TYPE.PDF]: () => [FILE_EXTENSION.PDF],
  [MEDIA_TYPE.MS_WORD]: () => [FILE_EXTENSION.DOC, FILE_EXTENSION.DOCX],
  [MEDIA_TYPE.MS_EXCEL]: () => [FILE_EXTENSION.XLS, FILE_EXTENSION.XLSX],
  [MEDIA_TYPE.CSV]: () => [FILE_EXTENSION.CSV]
};

const MEDIA_ID_DATA_ATTRIBUTE = 'data-media-id';

const MIME_TYPE = {
  IMAGE_JPEG: 'image/jpeg',
  IMAGE_PNG: 'image/png',
  IMAGE_GIF: 'image/gif',
  PDF: 'application/pdf',
  MS_WORD_DOC: 'application/msword',
  MS_WORD_DOCX:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  MS_EXCEL_XLS: 'application/vnd.ms-excel',
  MS_EXCEL_XLSX:
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  CSV: 'text/csv'
};

const MIME_TYPES_BY_MEDIA_TYPE = {
  [MEDIA_TYPE.IMAGE]: [
    MIME_TYPE.IMAGE_JPEG,
    MIME_TYPE.IMAGE_PNG,
    MIME_TYPE.IMAGE_GIF
  ],
  [MEDIA_TYPE.PDF]: [MIME_TYPE.PDF],
  [MEDIA_TYPE.MS_WORD]: [MIME_TYPE.MS_WORD_DOC, MIME_TYPE.MS_WORD_DOCX],
  [MEDIA_TYPE.MS_EXCEL]: [MIME_TYPE.MS_EXCEL_XLS, MIME_TYPE.MS_EXCEL_XLSX],
  [MEDIA_TYPE.CSV]: [MIME_TYPE.CSV]
};

const EXTENSIONS_BY_MIME_TYPE = {
  [MIME_TYPE.IMAGE_JPEG]: [FILE_EXTENSION.JPEG, FILE_EXTENSION.JPG],
  [MIME_TYPE.IMAGE_PNG]: [FILE_EXTENSION.PNG],
  [MIME_TYPE.PDF]: [FILE_EXTENSION.PDF],
  [MIME_TYPE.MS_WORD_DOC]: [FILE_EXTENSION.DOC],
  [MIME_TYPE.MS_WORD_DOCX]: [FILE_EXTENSION.DOCX],
  [MIME_TYPE.MS_EXCEL_XLS]: [FILE_EXTENSION.XLS],
  [MIME_TYPE.MS_EXCEL_XLSX]: [FILE_EXTENSION.XLSX],
  [MIME_TYPE.CSV]: [FILE_EXTENSION.CSV]
};

/**
 * MIME Types are used to identify file type from BLOB object (.type prop)
 * @link https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Complete_list_of_MIME_types
 */

const FILE_FIELD = {
  TITLE: 'TITLE',
  ALT_TEXT: 'ALT_TEXT',
  CAPTION: 'CAPTION'
};

const VIDEO_FIELD = {
  URL: 'URL',
  TITLE: 'TITLE',
  DESCRIPTION: 'DESCRIPTION',
  PUBLISHED_AT: 'PUBLISHED_AT',
  THUMBNAIL_URL: 'THUMBNAIL_URL'
};

const MEDIA_ITEM_SOURCE = {
  MEDIA_ITEM_OBJECT: 'MEDIA_ITEM_OBJECT',
  BLOB: 'BLOB'
};

const MAX_IMAGE_FILE_SIZE = 30; // 30Mb

const MEDIA_VALIDATION = {
  FILE_FORMAT: 'FILE_FORMAT',
  MAX_FILE_SIZE: 'MAX_FILE_SIZE',
  REQUIRED_DIMENSIONS: 'REQUIRED_DIMENSIONS',
  FILE_DOWNLOAD: 'FILE_DOWNLOAD',
  CORRUPTED_PDF: 'CORRUPTED_PDF',
  PROTECTED_PDF: 'PROTECTED_PDF'
};

const MEDIA_V2_FILTER_REMAP_OBJECT = {
  searchPhrase: 'Filter.SearchPhrase',
  articleTopic: 'Filter.ArticleTopic',
  aspectRatios: 'Filter.AspectRatios',
  mediaTypes: 'Filter.MediaTypes',
  isOld: 'Filter.IsOld'
};

export { BACKEND_ENUM_BY_HTML_REL_ATTRIBUTE_VALUE, BASE_HTML_REL_ATTRIBUTE_VALUES, BASE_REL_ATTRIBUTE_BACKEND_VALUES, CHARS_TO_ESCAPE, CHARS_TO_SPLIT_PARAGRAPHS, COMMENTED_HTML_REGEXP, COMMENTED_HTML_REGEXP_STRING, CUSTOM_HTML_TAG, CUSTOM_HTML_TAG_VALUES, EMPTY_TEXT_CHARS, EXTENSIONS_BY_MIME_TYPE, FILE_EXTENSION, FILE_FIELD, HTML_REL_ATTRIBUTE_FOLLOW_VALUE, HTML_REL_ATTRIBUTE_VALUE, HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM, HTML_TAG, IMAGE_ALIGNMENT, IMAGE_BASE_64_REGEXP, IMAGE_SETTINGS, INLINE_EXCLUSION_ELEMENTS_TO_UNWRAP_FROM_PARAGRAPH, INLINE_HTML_TAGS, KEY_CODE, MAX_IMAGE_FILE_SIZE, MEDIA_ID_DATA_ATTRIBUTE, MEDIA_ITEM_SOURCE, MEDIA_TYPE, MEDIA_TYPE_FORMATS, MEDIA_V2_FILTER_REMAP_OBJECT, MEDIA_VALIDATION, MIME_TYPE, MIME_TYPES_BY_MEDIA_TYPE, REL_ATTRIBUTE_BACKEND_ENUM, REL_ATTRIBUTE_BACKEND_ENUM_BY_LEGACY_HTML_REL_ATTRIBUTE, REMOVE_EMPTY_TAGS_EXCLUSION_TAG_LIST, REMOVE_INLINE_STYLES_EXCLUSION_TAG_LIST, REPLACE_DIV_WITH_PARAGRAPH_EXCLUSION_PARENT_TAG_LIST, SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING, SPECIAL_CHAR, TABLE_INSIDE_ARTICLE_DATA_ATTRIBUTE, TARGET, TWITTER_CARD_CLASS_NAME, TWITTER_CARD_SCRIPT, TWITTER_CARD_SCRIPT_SOURCE, VIDEO_FIELD, WRAP_INLINE_ELEMENTS_TO_PARAGRAPH_EXCLUSION_TAG_LIST };
